import { useStoreState } from 'easy-peasy';
import { useMemo } from 'react';
import { useQuery, useQueryClient, useMutation } from 'react-query';

import { QueryKey, ViewBy } from '@ge/models/constants';
import { useTableFilter } from '@ge/shared/data-hooks/use-table-filter';
import {
  getEventManagementList,
  createEventManagement,
  updateEventManagement,
  putEventManagement,
  getProcProperties,
  getAllEventCodes,
  getAllCustomCodes,
  getAutomationSequences,
} from '@ge/shared/services/event-management';
import { sorter } from '@ge/util/metric-sorter';

import { Config } from './config';

export const useEM = ({
  search,
  filters,
  sortDirection,
  sortMetric,
  mapId,
  viewBy,
  selectedCustomEventId,
}) => {
  const { getSiteById } = useStoreState((state) => state.sites);
  let { data, error, isLoading, isFetching } = useQuery(
    [QueryKey.EVENT_MANAGEMENT_LIST, mapId, viewBy],
    () => getEventManagementList({ mapId }),
    {
      refetchOnMount: true,
      refetchOnReconnect: true,
      enabled: mapId != null ? true : false,
    },
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tempFilterValues = {
    includedsites: [],
    excludedsites: [],
    procedurehandling: [],
    notes: [],
    eventmap: [],
    automationsequence: [],
    parentEventIds: [],
    childEventIds: [],
  };
  const recordsFn = useMemo(() => {
    const records = [];
    data &&
      data.length &&
      data.forEach((eventMap) => {
        if (viewBy === ViewBy.EVENT && eventMap.eventMapId === 'custom_cases') {
          return;
        }

        if (viewBy === ViewBy.Custom && eventMap.eventMapId !== 'custom_cases') {
          return;
        }
        eventMap?.events?.forEach((rec) => {
          if (viewBy === ViewBy.Custom) {
            if (!selectedCustomEventId) {
              return;
            }

            if (selectedCustomEventId === 'All') {
              // do NOT filter by eventId
            }
            // Specific event selected → filter
            else if (rec.eventId !== selectedCustomEventId) {
              return;
            }
          }

          const value = rec;
          if (value?.flaggedProperties) {
            ['include', 'exclude'].forEach((record) => {
              if (value.flaggedProperties[`${record}SitesFilter`]?.length) {
                value[`${record}dsites`] = {
                  value: value.flaggedProperties[`${record}SitesFilter`]
                    ?.map((val) => getSiteById(val.siteId)?.name)
                    .filter((val) => !!val),
                };
                tempFilterValues[`${record}dsites`] = [
                  ...(tempFilterValues[`${record}dsites`] || []),
                  ...(value[`${record}dsites`]?.value || []),
                ];
              }
            });
          }
          if (value?.automationProperties?.procedure) {
            value['automationsequence'] = {
              value: value?.automationProperties?.procedure.map((Obj) =>
                Obj.name?.replaceAll('_', ' '),
              ),
            };
          }
          value.sequenceName = value?.automationsequence?.value.join(', ') || '';
          value.blockCommands = (value?.commandProperties?.blockCommands || false).toString();
          value.blockAutomation = (
            value?.automationProperties?.blockAutomation || false
          ).toString();
          value.blockCaseEscalation = (
            value?.automationProperties?.blockCaseEscalation || false
          ).toString();
          value.maxResetLimit = (value?.commandProperties?.maxResetLimit || 0).toString();
          value.presentationDelay = (value?.caseProperties?.presentationDelay || 0).toString();
          value.caseCreationDelay = (value?.caseProperties?.caseCreationDelay || 0).toString();
          value.flaggedStates = { value: value?.flaggedProperties?.eligableStates || [] };
          value.procedurehandling = value?.procProperties?.procTitle || '';
          value.eventMapName = eventMap?.eventMapName || '';
          value.eventMapId = eventMap?.eventMapId || '';
          value.notes = value?.note || '';
          value.eventmap = eventMap?.eventMapName || '';
          tempFilterValues.flaggedStates = [
            ...(tempFilterValues.flaggedStates || []),
            ...(value?.flaggedStates?.value || []),
          ];
          tempFilterValues.procedurehandling = [
            ...(tempFilterValues.procedurehandling || []),
            value?.procedurehandling || '',
          ];
          tempFilterValues.notes = [...(tempFilterValues.notes || []), value?.notes || ''];
          tempFilterValues.eventmap = [...(tempFilterValues.eventmap || []), value?.eventmap || ''];
          tempFilterValues.automationsequence = [
            ...(tempFilterValues.automationsequence || []),
            ...(value?.automationsequence?.value || []),
          ];
          tempFilterValues.parentEventIds = [
            ...(tempFilterValues.parentEventIds || []),
            ...(value?.parentEventIds || []),
          ];
          tempFilterValues.childEventIds = [
            ...(tempFilterValues.childEventIds || []),
            ...(value?.childEventIds || []),
          ];
          value.parentEventIdsStr = value?.parentEventIds?.join(', ') || '';
          value.childEventIdsStr = value?.childEventIds?.join(', ') || '';
          records.push(value);
        });
      });
    return records;
  }, [data, getSiteById, tempFilterValues, viewBy, selectedCustomEventId]);
  if (filters?.code?.value) {
    recordsFn?.forEach((data) => (data.code = String(data.code)));
  } else {
    recordsFn?.forEach((data) => (data.code = Number(data.code)));
  }
  const { data: filteredData, filterValues } = useTableFilter({
    data: recordsFn,
    filters,
    search,
    filterSequence: [],
    isFleetTable: true,
  });

  if (filterValues) {
    filterValues.includedsites = [...new Set(tempFilterValues.includedsites)];
    filterValues.excludedsites = [...new Set(tempFilterValues.excludedsites)];
    filterValues.flaggedStates = [...new Set(tempFilterValues.flaggedStates)];
    filterValues.procedurehandling = [...new Set(tempFilterValues.procedurehandling)];
    filterValues.notes = [...new Set(tempFilterValues.notes)];
    filterValues.eventmap = [...new Set(tempFilterValues.eventmap)];
    filterValues.automationsequence = [...new Set(tempFilterValues.automationsequence)];
    filterValues.parentEventIds = [...new Set(tempFilterValues.parentEventIds)];
    filterValues.childEventIds = [...new Set(tempFilterValues.childEventIds)];
  }
  const code = filterValues?.code?.map(String);
  // cuts a new ref when sorting to update graph accordingly
  const sortedData = useMemo(
    () => (filteredData?.length ? [...filteredData.sort(sorter(sortMetric, sortDirection))] : []),
    [filteredData, sortDirection, sortMetric],
  );
  const escalationRules = useMemo(() => {
    const map = new Map();

    data?.forEach((eventMap) => {
      if (eventMap.eventMapId !== 'custom_cases') return;

      eventMap?.events?.forEach((rec) => {
        map.set(String(rec.eventId), rec?.automationProperties?.blockCaseEscalation === true);
      });
    });

    return map;
  }, [data]);

  return {
    eventManagementLists: sortedData,
    escalationRules,
    filterValues: { ...filterValues, code: code },
    error,
    isLoading,
    isFetching,
  };
};

export const useEventValidationList = ({ id, mapId }) => {
  let { data, error, isLoading } = useQuery(
    [QueryKey.EVENT_MANAGEMENT_LIST, id, mapId],
    () => getEventManagementList({ id, mapId }),
    {
      ...Config.EXECUTE_ONCE,
      enabled: !!(id && mapId),
    },
  );

  let details = data && data[0]?.events[0];
  return { isExist: data?.length, error, isLoading, details };
};

export const useCreateEventManagement = () => {
  const queryCache = useQueryClient();

  const {
    mutateAsync: createEM,
    data,
    isLoading,
    isError,
    error,
  } = useMutation(createEventManagement, {
    onSuccess: () => queryCache.invalidateQueries(QueryKey.EVENT_MANAGEMENT_LIST, { exact: true }),
    enabled: true,
    ...Config.EXECUTE_ONCE,
  });
  return useMemo(
    () => ({ createEM, data, isLoading, isError, error }),
    [createEM, data, isLoading, isError, error],
  );
};

export const useUpdateEventManagement = () => {
  const queryCache = useQueryClient();

  const {
    mutateAsync: updateEM,
    data,
    isLoading,
    isError,
    error,
  } = useMutation(putEventManagement, {
    onSuccess: () => queryCache.invalidateQueries(QueryKey.EVENT_MANAGEMENT_LIST),
    enabled: true,
    ...Config.EXECUTE_ONCE,
  });

  return useMemo(
    () => ({ updateEM, data, isLoading, isError, error }),
    [updateEM, data, isLoading, isError, error],
  );
};

export const useProcProperties = (id) => {
  let { data } = useQuery([QueryKey.EVENT_MANAGEMENT_LIST, id], () => getProcProperties({ id }), {
    ...Config.EXECUTE_ONCE,
    enabled: !!id,
    select: (data) => data[0],
  });

  return { data };
};

export const useAllAutomationSequence = (eventCode) => {
  let { data } = useQuery(
    [QueryKey.AUTOMATION_SEQUENCE_LIST, eventCode],
    () => getAutomationSequences(),
    {
      ...Config.EXECUTE_ONCE,
      enabled: eventCode,
    },
  );

  return { data };
};

export const useEditEventManagement = () => {
  const queryCache = useQueryClient();

  const {
    mutateAsync: editEM,
    data,
    isLoading,
    isError,
    error,
  } = useMutation(updateEventManagement, {
    onSuccess: () => queryCache.invalidateQueries(QueryKey.EVENT_MANAGEMENT_LIST),
    enabled: true,
    ...Config.EXECUTE_ONCE,
  });

  return useMemo(
    () => ({ editEM, data, isLoading, isError, error }),
    [editEM, data, isLoading, isError, error],
  );
};

export const useAllEventCode = (eventCode) => {
  let { data, error, isLoading, isFetching } = useQuery(
    [QueryKey.EVENT_CODES_LIST, eventCode],
    () => getAllEventCodes(),
    {
      refetchOnMount: true,
      refetchOnReconnect: true,
      enabled: eventCode,
    },
  );
  const uniqueEventCodes = useMemo(() => {
    return getAllEventCode(data);
  }, [data]);

  return {
    eventCodeList: uniqueEventCodes,
    error,
    isLoading,
    isFetching,
  };
};

export const useAllCustomCode = (customCode) => {
  const { data } = useQuery(
    [QueryKey.CUSTOM_CODES_LIST, customCode],
    () => getAllCustomCodes(customCode),
    {
      refetchOnMount: true,
      refetchOnReconnect: true,
      enabled: customCode,
    },
  );
  const uniqueEventCodes = useMemo(() => {
    return getAllCustomCode(data);
  }, [data]);
  return {
    customCodeList: uniqueEventCodes,
  };
};

const getAllEventCode = (data) => {
  let uniqueEventCodes = new Set();
  if (!data || !Array.isArray(data)) {
    return [];
  }

  for (const eventMap of data) {
    if (!eventMap || !eventMap.events || !Array.isArray(eventMap.events)) {
      continue;
    }
    for (const events of eventMap.events) {
      const sourceId = events.sourceEventId;
      if (sourceId) {
        uniqueEventCodes.add(sourceId);
      }
    }
  }
  return Array.from(uniqueEventCodes)
    .map(Number)
    .sort((a, b) => a - b);
};

const getAllCustomCode = (data) => {
  if (!Array.isArray(data)) {
    return [];
  }

  const uniqueEventCodes = new Set();

  for (const eventMap of data) {
    if (eventMap?.eventMapId === 'custom_cases' && eventMap?.eventId) {
      uniqueEventCodes.add(String(eventMap.eventId));
    }
  }

  const sortedCodes = Array.from(uniqueEventCodes).sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true })
  );

  return [...sortedCodes, 'All'];
};

